<template>
  <div class="seg-vista">
    <div class="header-flex">
      <div>
        <h1>Informe de Seguimiento al Plan</h1>
        <p class="subtitulo">Ejecución #{{ idEjecucion }}<template v-if="datos"> · {{ datos.portada.programa }} · {{ datos.portada.periodo }}</template></p>
      </div>
      <button class="btn salir" @click="$router.push({ name: 'ProcesoAutoevaluacionUsuario' })">Volver</button>
    </div>

    <!-- Selector de tipo (dos documentos: mismo plan filtrado por tipo de acción) -->
    <div class="tabs">
      <button class="tab" :class="{ activo: tipo === 'mejoramiento' }" @click="cambiarTipo('mejoramiento')">Plan de Mejoramiento</button>
      <button class="tab" :class="{ activo: tipo === 'fortalecimiento' }" @click="cambiarTipo('fortalecimiento')">Plan de Fortalecimiento</button>
    </div>

    <div v-if="cargando" class="estado-msg">Cargando…</div>
    <div v-else-if="error" class="estado-msg error">{{ error }}</div>

    <template v-else-if="datos">
      <div class="acciones-top">
        <span v-if="guardadoOk" class="ok-msg">Cambios guardados ✓</span>
        <button class="btn" :disabled="guardando" @click="guardar">{{ guardando ? 'Guardando…' : 'Guardar cambios' }}</button>
        <button class="btn primario" :disabled="descargando" @click="descargar">{{ descargando ? 'Generando…' : 'Descargar PDF' }}</button>
      </div>

      <!-- ═══ 1 · Portada ═══ -->
      <section class="bloque">
        <h2>1 · Portada</h2>
        <div class="grid-portada">
          <label>Anexo<input v-model="ed.anexo" type="text" placeholder="ANEXO 2." /></label>
          <label>Condición<input v-model="ed.condicion" type="text" /></label>
          <label>Código SNIES<input v-model="ed.codigo_snies" type="text" /></label>
          <label>Resolución<input v-model="ed.resolucion" type="text" /></label>
          <label>Lugar<input v-model="ed.lugar" type="text" placeholder="Bucaramanga" /></label>
          <label>Fecha de elaboración<input v-model="ed.fecha_elaboracion" type="date" /></label>
        </div>
        <p class="nota">Título del documento: <b>{{ datos.portada.titulo }}</b> · Facultad: {{ datos.portada.facultad ?? '—' }}</p>
      </section>

      <!-- ═══ 2 · Presentación ═══ -->
      <section class="bloque">
        <h2>2 · Presentación</h2>
        <textarea v-model="ed.presentacion" rows="5"></textarea>
      </section>

      <!-- ═══ 3 · Informe de seguimiento (intro) ═══ -->
      <section class="bloque">
        <h2>3 · Informe de seguimiento</h2>
        <textarea v-model="ed.informe_intro" rows="4"></textarea>
      </section>

      <!-- ═══ 4 · Tablas por factor (derivadas del plan) ═══ -->
      <section class="bloque">
        <h2>4 · Seguimiento por factor</h2>
        <p v-if="datos.sin_plan || !datos.factores.length" class="nota">
          La ejecución no tiene un plan de {{ tipo }} con acciones. Genera y diligencia el
          <router-link :to="{ name: 'PlanMejoramiento', params: { id_ejecucion: idEjecucion } }">plan de mejoramiento</router-link> primero.
        </p>
        <template v-else>
          <p class="nota">Los datos de estas tablas se toman del plan; edítalos en el plan de mejoramiento (aspecto, meta, estrategias, balance de cumplimiento).</p>
          <div v-for="f in datos.factores" :key="f.factor" class="factor">
            <h3>{{ f.factor }}</h3>
            <div class="contenedor-tabla">
              <table class="lista-tabla">
                <thead>
                  <tr>
                    <th>Aspecto por mejorar</th>
                    <th>Meta trazada</th>
                    <th>Medición</th>
                    <th>Por cumplir</th>
                    <th>Periodo</th>
                    <th>Estrategias implementadas</th>
                    <th class="ctr">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="fila in f.filas" :key="fila.id_accion">
                    <td>{{ fila.aspecto }}</td>
                    <td>{{ fila.meta }}</td>
                    <td>{{ fila.medicion }}</td>
                    <td>{{ fila.por_cumplir }}</td>
                    <td class="ctr">{{ rango(fila.fecha_inicio, fila.fecha_terminacion) }}</td>
                    <td>{{ fila.estrategias }}</td>
                    <td class="ctr"><span class="pill" :class="pillClase(fila.balance)">{{ fila.balance === null ? '—' : fila.balance + '%' }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </section>

      <!-- ═══ 5 · Valoración final ═══ -->
      <section class="bloque">
        <h2>5 · Valoración final</h2>
        <p class="resumen-linea">
          Cumplimiento promedio: <b>{{ datos.resumen.promedio }}%</b> ·
          {{ datos.resumen.n_aspectos }} aspecto(s).
          <span class="nota">Usa <code>{promedio}</code> y <code>{aspectos}</code> como comodines en el texto.</span>
        </p>
        <textarea v-model="ed.valoracion_final" rows="5"></textarea>
        <p class="nota">Vista previa: {{ valoracionPreview }}</p>
      </section>
    </template>
  </div>
</template>

<script lang="ts" setup>
/**
 * Seguimiento periódico del plan de mejoramiento.
 *
 * La alerta C6 avisa de los planes con acciones abiertas y sin seguimiento en 90
 * días.
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import {
  getSeguimientoPlanService,
  guardarSeguimientoPlanService,
  descargarPdfSeguimientoService,
  type SeguimientoPlan,
  type TipoSeguimiento,
  type SeguimientoEditable
} from '@/services/SeguimientoPlanService'

const route = useRoute()
const idEjecucion = Number(route.params.id_ejecucion)

const tipo = ref<TipoSeguimiento>('mejoramiento')
const datos = ref<SeguimientoPlan | null>(null)
const cargando = ref(false)
const error = ref('')
const guardando = ref(false)
const guardadoOk = ref(false)
const descargando = ref(false)

const ed = reactive<SeguimientoEditable>({})

function fmt(d: string | null): string {
  if (!d) return ''
  const p = new Date(d)
  return isNaN(p.getTime()) ? '' : p.toLocaleDateString('es-CO')
}
function rango(i: string | null, f: string | null): string {
  return i || f ? `${fmt(i)} – ${fmt(f)}`.trim() : ''
}
function pillClase(v: number | null): string {
  if (v === null) return 'p-gris'
  return v >= 70 ? 'p-verde' : v >= 40 ? 'p-ambar' : 'p-rojo'
}

const valoracionPreview = computed(() => {
  if (!datos.value) return ''
  return (ed.valoracion_final ?? '')
    .replace(/\{promedio\}/g, String(datos.value.resumen.promedio))
    .replace(/\{aspectos\}/g, String(datos.value.resumen.n_aspectos))
})

async function cargar(): Promise<void> {
  cargando.value = true
  error.value = ''
  const r = await getSeguimientoPlanService(idEjecucion, tipo.value)
  if (r.ok && r.data) {
    datos.value = r.data
    ed.anexo = r.data.portada.anexo
    ed.condicion = r.data.portada.condicion
    ed.codigo_snies = r.data.portada.codigo_snies
    ed.resolucion = r.data.portada.resolucion
    ed.lugar = r.data.portada.lugar
    ed.fecha_elaboracion = r.data.portada.fecha_elaboracion
    ed.presentacion = r.data.narrativa.presentacion
    ed.informe_intro = r.data.narrativa.informe_intro
    ed.valoracion_final = r.data.narrativa.valoracion_final
  } else {
    error.value = r.error ?? 'No se pudo cargar'
    datos.value = null
  }
  cargando.value = false
}

function cambiarTipo(t: TipoSeguimiento): void {
  if (tipo.value === t) return
  tipo.value = t
  cargar()
}

async function guardar(): Promise<void> {
  guardando.value = true
  guardadoOk.value = false
  const r = await guardarSeguimientoPlanService(idEjecucion, tipo.value, { ...ed })
  guardando.value = false
  if (r.ok) {
    guardadoOk.value = true
    setTimeout(() => (guardadoOk.value = false), 2500)
  } else {
    error.value = r.error ?? 'No se pudo guardar'
  }
}

async function descargar(): Promise<void> {
  descargando.value = true
  await descargarPdfSeguimientoService(idEjecucion, tipo.value)
  descargando.value = false
}

onMounted(cargar)
</script>

<style scoped>
.seg-vista { padding: 1.2rem 1.4rem; max-width: 1100px; }
.header-flex { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; }
h1 { color: #0e69a0; margin: 0 0 0.2rem; }
.subtitulo { color: #666; margin: 0; font-size: 0.86rem; }
.btn.salir { border: 1px solid #bbb; background: #fff; color: #444; padding: 0.35rem 0.9rem; border-radius: 6px; cursor: pointer; }

.tabs { display: flex; gap: 0.4rem; margin: 1rem 0; border-bottom: 2px solid #e2e2e2; }
.tab { border: none; background: none; padding: 0.5rem 1rem; cursor: pointer; color: #666; border-bottom: 3px solid transparent; margin-bottom: -2px; font-size: 0.9rem; }
.tab.activo { color: #0e69a0; border-bottom-color: #0e69a0; font-weight: 600; }

.estado-msg { padding: 1rem; color: #666; }
.estado-msg.error { color: #a32d2d; }

.acciones-top { display: flex; justify-content: flex-end; align-items: center; gap: 0.6rem; margin-bottom: 0.6rem; }
.ok-msg { color: #0f6e56; font-size: 0.84rem; }
.btn { border: 1px solid #0e69a0; background: #fff; color: #0e69a0; padding: 0.35rem 0.9rem; border-radius: 6px; cursor: pointer; font-size: 0.85rem; }
.btn:hover { background: #eef3f8; }
.btn.primario { background: #0e69a0; color: #fff; }
.btn.primario:hover { background: #0b587f; }
.btn:disabled { opacity: 0.55; cursor: default; }

.bloque { border: 1px solid #e6e6e6; border-radius: 8px; padding: 0.9rem 1.1rem; margin-bottom: 1rem; }
.bloque h2 { color: #0e69a0; font-size: 1.05rem; margin: 0 0 0.7rem; }
.bloque h3 { color: #0e69a0; font-size: 0.95rem; margin: 0.9rem 0 0.4rem; }

.grid-portada { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.7rem; }
.grid-portada label { display: flex; flex-direction: column; font-size: 0.78rem; color: #555; gap: 0.2rem; }
input, textarea { border: 1px solid #ccc; border-radius: 5px; padding: 0.4rem 0.5rem; font-size: 0.85rem; font-family: inherit; width: 100%; box-sizing: border-box; }
textarea { resize: vertical; }

.nota { font-size: 0.78rem; color: #777; font-style: italic; margin: 0.4rem 0; }
.resumen-linea { font-size: 0.88rem; margin-bottom: 0.5rem; }
code { background: #f0f0f0; padding: 0 4px; border-radius: 3px; font-style: normal; }

.contenedor-tabla { overflow-x: auto; }
.lista-tabla { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
.lista-tabla th { background: #0e69a0; color: #fff; padding: 0.45rem 0.5rem; text-align: left; font-weight: 600; white-space: nowrap; }
.lista-tabla td { border: 1px solid #e2e2e2; padding: 0.4rem 0.5rem; vertical-align: top; }
.ctr { text-align: center; }
.pill { display: inline-block; padding: 1px 8px; border-radius: 9px; font-size: 0.74rem; font-weight: 700; }
.p-verde { background: #d9efe4; color: #0f6e56; }
.p-ambar { background: #fdeecd; color: #854f0b; }
.p-rojo { background: #fadddd; color: #a32d2d; }
.p-gris { background: #ececec; color: #666; }
</style>
